import { calculatePrice, formatPrice, getSubscriptionText, makePricingExplanationForEvaluation, makePricingExplanationForSubscription } from '@atlassian/mpac-pricing-utils';
import { BodyNormal, BodySmall, Stripe } from '@atlassian/mpac-primitives';
import { DateUtils } from '@atlassian/mpac-utils';
import { List } from 'immutable';
import { useGetCmtAvailability, usePricingPlan } from '../../../hooks';
import { getPricingPlanItems } from '../OneClickAppInstallDialog.util';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const isAppPrice = calculatePriceResult => !!calculatePriceResult && typeof calculatePriceResult.amount === 'number' && typeof calculatePriceResult.currencyCode === 'string' && calculatePriceResult.currencyCode.trim().length > 0;
const getTrialDetails = cmtAvailability => {
  if (cmtAvailability && cmtAvailability.allowed && cmtAvailability.maintenanceEndDate) {
    const expiryDate = new Date(cmtAvailability.maintenanceEndDate);
    if (!isNaN(expiryDate.getTime())) {
      return {
        trialType: 'Cloud Migration Trial',
        trialEndDate: expiryDate
      };
    }
  }
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  return {
    trialType: 'free trial',
    trialEndDate: expiryDate
  };
};
export const PaidAppPricing = _ref => {
  let {
    appDetails,
    hostStatus,
    installedAppDetailsResult,
    selectedSiteAndProduct
  } = _ref;
  const {
    billingCurrency,
    hostLicense,
    upmLicensedHostUsers,
    billingSystem,
    hostCmtEnabled
  } = hostStatus.marketplaceStore.hostStatus;
  const {
    pricingPlanResult,
    pricingPlanPending,
    pricingPlanError
  } = usePricingPlan(appDetails.appId, billingCurrency);
  const {
    cmtAvailabilityResult
  } = useGetCmtAvailability(appDetails.appKey, billingSystem, hostCmtEnabled, selectedSiteAndProduct);
  if (pricingPlanPending) {
    return /*#__PURE__*/_jsx(Stripe, {
      width: "5rem",
      height: "0.875rem",
      marginTop: "var(--ds-space-075, 6px)"
    });
  }
  if (pricingPlanError || !pricingPlanResult) {
    return /*#__PURE__*/_jsx(BodyNormal, {
      children: "Unknown Price"
    });
  }
  const pricingPlanItems = getPricingPlanItems(pricingPlanResult);
  const {
    subscriptionAnnual
  } = hostLicense;
  const pricingPlanItemsBySubscription = subscriptionAnnual ? List(pricingPlanItems.annual) : List(pricingPlanItems.monthly);
  const appPrice = calculatePrice(pricingPlanItemsBySubscription, upmLicensedHostUsers);
  if (!appPrice || !isAppPrice(appPrice.value)) {
    return /*#__PURE__*/_jsx(BodyNormal, {
      children: "Unknown Price"
    });
  }
  const {
    amount,
    currencyCode
  } = appPrice.value;
  const installedApp = installedAppDetailsResult === null || installedAppDetailsResult === void 0 ? void 0 : installedAppDetailsResult.marketplaceStore.installedAppDetails;
  const renderPricingExplaination = () => {
    if (installedApp && installedApp.paidLicenseActiveOnParent) {
      return 'This app is already purchased and will continue to be available for free inside your sandbox.';
    }
    const subscriptionType = subscriptionAnnual ? 'annual' : 'monthly';
    if (!installedApp || !(installedApp !== null && installedApp !== void 0 && installedApp.licenseActive)) {
      const subscriptionText = getSubscriptionText(pricingPlanItemsBySubscription, upmLicensedHostUsers, appPrice.value, subscriptionType);
      const cmtAvailability = cmtAvailabilityResult === null || cmtAvailabilityResult === void 0 ? void 0 : cmtAvailabilityResult.marketplaceStore.cmtAvailability;
      const {
        trialEndDate,
        trialType
      } = getTrialDetails(cmtAvailability);
      return "If you proceed, your ".concat(trialType, " will start today and end on ").concat(DateUtils.formatDateObject(trialEndDate, 'MMM dd, yyyy'), ". After that, the cost will be customised for your ").concat(subscriptionText, ", as mentioned below:");
    }
    if (!installedApp.licenseExpiryDate || !DateUtils.isTodayOrFutureDate(new Date(installedApp.licenseExpiryDate))) {
      return makePricingExplanationForSubscription(pricingPlanItemsBySubscription, upmLicensedHostUsers, appPrice.value, subscriptionType);
    }
    return makePricingExplanationForEvaluation(pricingPlanItemsBySubscription, upmLicensedHostUsers, appPrice.value, subscriptionType, DateUtils.longDate(installedApp.licenseExpiryDate));
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(BodySmall, {
      children: renderPricingExplaination()
    }), /*#__PURE__*/_jsxs(BodyNormal, {
      children: [/*#__PURE__*/_jsx("strong", {
        children: formatPrice(installedApp !== null && installedApp !== void 0 && installedApp.paidLicenseActiveOnParent ? 0 : amount, currencyCode)
      }), ' ', "per ", subscriptionAnnual ? 'year' : 'month']
    })]
  });
};